import React, { useEffect, useReducer } from "react";
import { initialState, reducer } from "../reducers";
import Tabs from "components/tabs";
import {
    HeaderSection,
    SubSection,
    SubSectionSpaced,
    SubSectionTable,
    SubSectionTableColumn,
    SubSectionTableData,
    SubSectionTableRow,
} from "components/attributes/panels/components/read-only-sections";
import { readOnlyValue } from "components/attributes/lib/attribute-data";

// panelInfo.allergens.items should have never existed but we likely have some data that will incorrectly persist that value so we are capturing that in this file <- 1/5/2022

export const ReadOnlyGuaranteedAnalysisPanel = (props) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const panelInfo = state.panels[state.selectedPanel];

    useEffect(() => {
        if (!props.value || !props.value.panels) {
            return;
        }

        dispatch({ type: "SAVE_PANELS", panels: props.value.panels || [], isDirty: false });
    }, []);

    const changeTab = (tabIndex) => {
        dispatch({ type: "SET_PANEL_INDEX", selectedPanel: tabIndex });
    };

    if (!panelInfo) {
        return null;
    }

    const servingSize =
        panelInfo.servingSize && panelInfo.servingSize.value ? `${panelInfo.servingSize.value} ${panelInfo.servingSize.uom.text}` : "";
    const measurementItems = panelInfo.analysis.items;
    const longestMeasurementsColumnIndex =
        measurementItems.length > 0
            ? measurementItems
                  .map(({ measurements = [] }) => measurements.length)
                  .indexOf(Math.max(...panelInfo.analysis.items.map(({ measurements = [] }) => measurements.length)))
            : null;
    const longestItemMeasurementInfo = measurementItems.length > 0 ? panelInfo.analysis.items[longestMeasurementsColumnIndex].measurements : null;

    return (
        <div className="container">
            <Tabs tabs={state.panels} reportSelectedTab={changeTab} selectedTab={state.selectedPanel} readOnly={true} />

            <div className="panel read-only">
                <HeaderSection label="Guaranteed Analysis" />
                <SubSection label="Panel Header" separatorVariant="thick">
                    {panelInfo.header}
                </SubSection>
                <SubSectionSpaced leftLabel="Serving Size" rightLabel={servingSize} separatorVariant="none" disableMb disablePb />
                <SubSection label="Servings Per Container" separatorVariant="thick">
                    {panelInfo.servingsPerContainer}
                </SubSection>
                <SubSection label="Guaranteed Analysis" headerVariant="small" separatorVariant="none" />
                {measurementItems.length > 0 && (
                    <SubSectionTable>
                        <thead>
                            <SubSectionTableRow>
                                <SubSectionTableColumn>Note Mark</SubSectionTableColumn>
                                <SubSectionTableColumn>Nutrient</SubSectionTableColumn>
                                <SubSectionTableColumn>Min/Max</SubSectionTableColumn>
                                {longestItemMeasurementInfo &&
                                    longestItemMeasurementInfo
                                        .filter(({ value }) => value)
                                        .map((value, index) => {
                                            return <SubSectionTableColumn key={index}>Measurement</SubSectionTableColumn>;
                                        })}
                            </SubSectionTableRow>
                        </thead>
                        <tbody>
                            {measurementItems &&
                                measurementItems.map(({ key, noteMark, nutrient, minMax, measurements = [] }) => {
                                    return (
                                        <SubSectionTableRow key={key}>
                                            <SubSectionTableData>
                                                {noteMark ? (
                                                    <>
                                                        {noteMark.value} {noteMark.text}
                                                    </>
                                                ) : (
                                                    ""
                                                )}
                                            </SubSectionTableData>
                                            <SubSectionTableData>{nutrient ? nutrient.text : ""}</SubSectionTableData>
                                            <SubSectionTableData>{minMax}</SubSectionTableData>
                                            {measurements
                                                .filter(({ value }) => value)
                                                .map(({ value, uom }, index) => {
                                                    return (
                                                        <SubSectionTableData key={`${value}_${index}`}>
                                                            {value} {uom.text}
                                                        </SubSectionTableData>
                                                    );
                                                })}
                                        </SubSectionTableRow>
                                    );
                                })}
                        </tbody>
                    </SubSectionTable>
                )}
                {
                    <SubSection label="Footnotes" separatorVariant="thick" headerVariant="small">
                        {panelInfo.footnotes.items.map(({ text, noteMark }, index) => {
                            return (
                                <div key={index} className="footnote">
                                    <div className="input-group">
                                        <b>{readOnlyValue(noteMark, "DropdownPickList")}</b>
                                        {text}
                                    </div>
                                </div>
                            );
                        })}
                    </SubSection>
                }

                <SubSection label="Ingredients" headerVariant="big" separatorVariant="none" />
                {panelInfo.ingredients.leadIn && (
                    <SubSection label="Ingredients Lead-in" separatorVariant="none">
                        {panelInfo.ingredients.leadIn}
                    </SubSection>
                )}

                {panelInfo.ingredients.items.length > 0 && (
                    <SubSection label="Ingredients" separatorVariant="thick">
                        {panelInfo.ingredients.items.map(({ text }) => text).join(", ")}
                    </SubSection>
                )}

                <SubSection label="Other information" headerVariant="big" separatorVariant="none" />
                {panelInfo.calorieContent && (
                    <SubSection label="Calorie Content" separatorVariant="none">
                        {panelInfo.calorieContent}
                    </SubSection>
                )}
                {panelInfo.healthClaims && panelInfo.aafcoStatement && (
                    <SubSection label="AAFCO Statement" separatorVariant="thick">
                        {panelInfo.aafcoStatement}
                    </SubSection>
                )}

                <SubSection label="Health Claims" headerVariant="big" separatorVariant="none" />
                {panelInfo.healthClaims && panelInfo.healthClaims.items.length > 0 && (
                    <SubSection label="Health Claims" separatorVariant="thick">
                        {readOnlyValue(panelInfo.healthClaims.items, "FeatureBullet")}
                    </SubSection>
                )}

                <SubSection label="Allergy information" headerVariant="big" separatorVariant="none" />
                {!panelInfo.allergens.hasOwnProperty("items") && panelInfo.allergens.contains.length > 0 && (
                    <SubSection label="Contains" separatorVariant="thick">
                        {readOnlyValue(panelInfo.allergens.contains, "FeatureBullet")}
                    </SubSection>
                )}

                {!panelInfo.allergens.hasOwnProperty("items") && panelInfo.allergens.mayContain.length > 0 && (
                    <SubSection label="May contain" separatorVariant="thick">
                        {readOnlyValue(panelInfo.allergens.mayContain, "FeatureBullet")}
                    </SubSection>
                )}

                {!panelInfo.allergens.hasOwnProperty("items") && panelInfo.allergens.doesNotContain.length > 0 && (
                    <SubSection label="Does not contain" separatorVariant="thick">
                        {readOnlyValue(panelInfo.allergens.doesNotContain, "FeatureBullet")}
                    </SubSection>
                )}
            </div>
        </div>
    );
};
